/**
 * 갤러리 크게 보기. 필로스 관리자 갤러리를 줄여 옮긴 것으로, 넘기기와 캡션만 남겼다.
 *
 * 이미지는 바깥 주소라 `next/image` 를 못 쓴다(도메인을 미리 등록할 수 없음).
 */

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@buildpy/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@buildpy/ui/dialog";
import { PortfolioImage } from "@buildpy/core/modules/portfolio/types";

interface Props {
  images: PortfolioImage[];
  /** null 이면 닫혀 있다. */
  openIndex: number | null;
  onClose: () => void;
}

const PortfolioLightbox = ({ images, openIndex, onClose }: Props) => {
  const [index, setIndex] = useState(0);

  // 바깥에서 「3번째를 열어라」 로 들어오므로 열릴 때마다 맞춰 준다.
  useEffect(() => {
    if (openIndex !== null) setIndex(openIndex);
  }, [openIndex]);

  const open = openIndex !== null && images.length > 0;
  const current = images[Math.min(index, images.length - 1)];
  const move = (step: number) =>
    setIndex((prev) => (prev + step + images.length) % images.length);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogTitle className="text-sm">
          {current?.caption || `이미지 ${index + 1}`}
          <span className="ml-2 font-normal text-muted-foreground">
            {index + 1} / {images.length}
          </span>
        </DialogTitle>
        {/* 화면에는 안 보이지만 읽어 주는 설명. 없으면 스크린리더가 「무엇을 여는
            창인지」 를 말해 주지 못하고, Radix 도 경고를 남긴다. */}
        <DialogDescription className="sr-only">
          시공사례 사진을 크게 봅니다. 좌우 화살표로 넘기고 Esc 로 닫습니다.
        </DialogDescription>

        <div className="flex items-center gap-x-2">
          <Button
            aria-label="이전 이미지"
            disabled={images.length < 2}
            size="icon"
            variant="outline"
            onClick={() => move(-1)}>
            <ChevronLeft className="size-4" />
          </Button>

          <div className="flex min-h-64 flex-1 items-center justify-center overflow-hidden rounded-md bg-muted">
            {current && (
              <img
                alt={current.caption || "시공사례 이미지"}
                className="max-h-[70vh] w-full object-contain"
                src={current.url}
              />
            )}
          </div>

          <Button
            aria-label="다음 이미지"
            disabled={images.length < 2}
            size="icon"
            variant="outline"
            onClick={() => move(1)}>
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PortfolioLightbox;
